// frontend/src/pages/LiveLeaderboard.js
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Crown, Medal, Award, Zap, Trophy } from "lucide-react";
import { useAuth } from "../App";
import GlassCard from "./MatchList/components/GlassCard";
import { colors } from "./MatchList/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LiveLeaderboard = ({ user }) => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [matchId]);

  const fetchData = async () => {
    try {
      const fetchFn = auth?.authFetch || fetch;
      const [lbRes, matchRes] = await Promise.all([
        fetchFn(`${BACKEND_URL}/api/matches/${matchId}/live-leaderboard`),
        fetchFn(`${BACKEND_URL}/api/matches/${matchId}`),
      ]);
      if (lbRes.ok) setData(await lbRes.json());
      if (matchRes.ok) setMatch(await matchRes.json());
    } finally {
      setLoading(false);
    }
  };

  const rankBadge = (rank) => {
    if (rank === 1) {
      return {
        Icon: Crown,
        color: "#FDE047",
        bg: "rgba(253,224,71,0.12)",
        border: "rgba(253,224,71,0.35)",
      };
    }
    if (rank === 2) {
      return {
        Icon: Medal,
        color: "#E5E7EB",
        bg: "rgba(229,231,235,0.1)",
        border: "rgba(229,231,235,0.3)",
      };
    }
    if (rank === 3) {
      return {
        Icon: Award,
        color: "#FB923C",
        bg: "rgba(251,146,60,0.1)",
        border: "rgba(251,146,60,0.3)",
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: colors.bg }}
      >
        <div className="w-8 h-8 border-2 border-[#01ff9a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const leaderboard = data?.leaderboard || [];
  const maxCheckpoints = data?.max_checkpoints || 8;
  const myId = user?.user_id || user?.id;

  return (
    <div className="min-h-screen" style={{ background: colors.bg }}>
      {/* Sticky header */}
      <div
        className="sticky top-0 z-40 backdrop-blur-xl"
        style={{
          background: "rgba(15,15,15,0.78)",
          borderBottom: `1px solid ${colors.cardBorder}`,
        }}
      >
        <div className="max-w-md mx-auto px-5 py-3.5 flex items-center justify-between">
          <button
            onClick={() => navigate(`/match/${matchId}`)}
            className="flex items-center gap-1.5 -ml-1 px-1.5 py-1.5 rounded-lg hover:bg-white/5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-white/80" />
            <span className="text-sm text-white/80">Match</span>
          </button>

          <div className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" style={{ color: colors.accent }} />
            <span
              className="text-[10px] font-semibold uppercase tracking-widest"
              style={{ color: colors.accent }}
            >
              Live Leaderboard
            </span>
          </div>

          <div className="w-[72px]" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 py-5 space-y-4">
        {/* Title + match */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <h1
            className="text-[26px] font-bold text-white leading-tight"
            style={{ fontFamily: "Syne, sans-serif" }}
          >
            Checkpoint points,<br />
            <span
              style={{
                background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              live standings.
            </span>
          </h1>
          {match && (
            <p className="text-[13px] text-white/50 mt-1.5">
              M{match.match_number} · {match.team_a} vs {match.team_b}
            </p>
          )}
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <GlassCard padding="p-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center">
                <p className="text-[10px] font-medium uppercase tracking-widest text-white/40 mb-1">
                  Players
                </p>
                <p className="text-2xl font-bold text-white tabular-nums">{leaderboard.length}</p>
              </div>
              <div
                className="text-center border-x"
                style={{ borderColor: colors.cardBorder }}
              >
                <p className="text-[10px] font-medium uppercase tracking-widest text-white/40 mb-1">
                  Checkpoints
                </p>
                <p className="text-2xl font-bold text-white tabular-nums">{maxCheckpoints}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium uppercase tracking-widest text-white/40 mb-1">
                  Top Score
                </p>
                <p
                  className="text-2xl font-bold tabular-nums"
                  style={{
                    background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {leaderboard.length > 0 ? leaderboard[0].total_points : 0}
                </p>
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Rankings */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between mb-2.5 px-1">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40">
              Rankings
            </p>
            <p className="text-[11px] text-white/30">Updates every 10s</p>
          </div>

          {leaderboard.length === 0 ? (
            <GlassCard padding="p-8" className="text-center">
              <Trophy className="w-10 h-10 mx-auto mb-3 text-white/20" />
              <p className="text-sm text-white/60 font-semibold">No predictions yet</p>
              <p className="text-[11px] text-white/30 mt-1">Be the first to play.</p>
            </GlassCard>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((entry, idx) => {
                const isMe = entry.user_id === myId;
                const badge = rankBadge(entry.rank);

                return (
                  <motion.div
                    key={entry.user_id}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(idx * 0.03, 0.3) }}
                    className="flex items-center gap-3 p-3.5 rounded-2xl"
                    style={
                      isMe
                        ? {
                            background:
                              "linear-gradient(90deg, rgba(1,255,154,0.14), rgba(172,255,229,0.08))",
                            border: `1px solid ${colors.accent}66`,
                            boxShadow: "0 0 24px rgba(1,255,154,0.12)",
                          }
                        : {
                            background: colors.cardBg,
                            border: `1px solid ${colors.cardBorder}`,
                          }
                    }
                  >
                    {/* Rank icon or number */}
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={
                        badge
                          ? {
                              background: badge.bg,
                              border: `1px solid ${badge.border}`,
                            }
                          : {
                              background: "rgba(255,255,255,0.04)",
                              border: `1px solid ${colors.cardBorder}`,
                            }
                      }
                    >
                      {badge ? (
                        <badge.Icon className="w-4 h-4" style={{ color: badge.color }} />
                      ) : (
                        <span className="text-xs font-bold text-white/60 tabular-nums">
                          {entry.rank}
                        </span>
                      )}
                    </div>

                    {/* Name + progress */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p
                          className={`text-sm font-semibold truncate ${
                            isMe ? "text-white" : "text-white/90"
                          }`}
                        >
                          {entry.username}
                        </p>
                        {isMe && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                            style={{
                              background: colors.accent,
                              color: "#0f0f0f",
                            }}
                          >
                            You
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div
                          className="flex-1 h-1 rounded-full overflow-hidden"
                          style={{ background: "rgba(255,255,255,0.06)" }}
                        >
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min(
                                (entry.checkpoints_predicted / maxCheckpoints) * 100,
                                100,
                              )}%`,
                              background: isMe
                                ? `linear-gradient(90deg, ${colors.accent}, ${colors.accentSecondary})`
                                : "rgba(255,255,255,0.3)",
                            }}
                          />
                        </div>
                        <p className="text-[10px] text-white/40 tabular-nums">
                          {entry.checkpoints_predicted}/{maxCheckpoints}
                        </p>
                      </div>
                    </div>

                    {/* Points */}
                    <div className="text-right flex-shrink-0">
                      <p
                        className="text-xl font-bold tabular-nums"
                        style={{ color: isMe ? colors.accent : colors.textPrimary }}
                      >
                        {entry.total_points}
                      </p>
                      <p className="text-[10px] text-white/40 -mt-0.5">pts</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Footer actions */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 gap-2.5 pt-2"
        >
          <button
            onClick={() => navigate(`/match/${matchId}`)}
            className="py-3.5 rounded-xl text-sm font-semibold text-white/80 transition-all active:scale-[0.98]"
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.cardBorder}`,
            }}
          >
            Back to Match
          </button>
          <button
            onClick={() => navigate("/")}
            className="py-3.5 rounded-xl text-sm font-bold transition-all active:scale-[0.98]"
            style={{
              background: "linear-gradient(90deg, #01ff9a, #acffe5)",
              color: "#0f0f0f",
            }}
          >
            All Matches
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default LiveLeaderboard;
